import { useCallback, useMemo } from "react";
import { getFieldValueFromSearchParams, getObjectFromSearchParams } from "../helpers/methods";
import { ORDERING_DIRECTION_ASC, ORDERING_DIRECTION_DESC } from "../helpers/constants";
import { usePaginationWithLocalSearchParams } from "./usePaginationWithLocalSearchParams";

/**
 * Alternative to usePaginationWithSearchParamsAndOrdering that keeps search params in local state
 *
 * @param filterPrefix - unused, for compatibility with usePaginationWithSearchParamsAndOrdering
 * @param initialParams - initial value for search params
 */
export default function usePaginationWithLocalSearchParamsAndOrdering(
    filterPrefix = undefined,
    initialParams = new URLSearchParams(),
) {
    const {
        page,
        pageSize,
        handleChangePageWithSearchParams,
        handleChangePageSizeWithSearchParams,
        resetPageNumber,
        searchParams,
        setSearchParams,
        handleChangeSearchParams,
    } = usePaginationWithLocalSearchParams(filterPrefix, initialParams);

    const sortParams = useMemo(() => {
        const ordering = getFieldValueFromSearchParams(searchParams, "ordering");
        if (!ordering) {
            return {};
        }
        if (ordering.startsWith("-")) {
            return {
                sortColumn: ordering.substring(1),
                sortOrder: ORDERING_DIRECTION_DESC,
            };
        }
        return {
            sortColumn: ordering,
            sortOrder: ORDERING_DIRECTION_ASC,
        };
    }, [searchParams]);

    const handleSort = useCallback(
        (sortColumn, sortOrder) => {
            resetPageNumber();
            const currSearchParamsObject = getObjectFromSearchParams(searchParams);
            let ordering = sortColumn;
            if (sortOrder === ORDERING_DIRECTION_DESC) {
                ordering = `-${ordering}`;
            }
            handleChangeSearchParams({
                ...currSearchParamsObject,
                ordering: ordering,
                page: 1,
            });
        },
        [searchParams, handleChangeSearchParams, resetPageNumber],
    );

    return {
        page,
        pageSize,
        handleChangePageWithSearchParams,
        handleChangePageSizeWithSearchParams,
        resetPageNumber,
        searchParams,
        setSearchParams,
        handleChangeSearchParams,
        sortParams,
        handleSort,
    };
}
